// src/pages/HomePage.jsx – the multilingual home page, and the per-feature landing pages (?page=…)
import React, { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  Stack,
  Box,
  FormControl,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { initKeycloak } from '../keycloak';
import '../i18n';
import BookDemoDialog from './landing/BookDemoDialog';
import KioskLanding from './landing/KioskLanding';
import VerifiedLanding from './landing/VerifiedLanding';
import ConciergerieLanding from './landing/ConciergerieLanding';
import StatsLanding from './landing/StatsLanding';
import EventsLanding from './landing/EventsLanding';
import { recordLandingVisit } from '../utils/attribution';

// Assets
import counterKioskImg from '../assets/onboarding-counter-kiosk.jpg';

// One landing page per feature, for ads and campaigns: qrproofs.com/?page=borne
// (backend/utils/attribution.ts keeps the same list). Without ?page, or with
// an unknown one, the regular home page below.
const LANDINGS = {
  borne: {
    Component: KioskLanding,
    title: 'Borne de notation · QRProofs',
    description: 'La borne QRProofs : vos clients notent sur place, avec un QR code qui change toutes les 10 secondes.',
  },
  'avis-verifies': {
    Component: VerifiedLanding,
    title: 'Avis vérifiés · QRProofs',
    description: 'Zéro faux avis : chaque note QRProofs est donnée sur place, par un client venu chez vous.',
  },
  conciergerie: {
    Component: ConciergerieLanding,
    title: 'Conciergerie · QRProofs',
    description: 'Gîtes et hôtels : vos voyageurs signalent un souci pendant le séjour, vous le réglez avant l\'avis.',
  },
  statistiques: {
    Component: StatsLanding,
    title: 'Statistiques et quartier · QRProofs',
    description: 'Votre note face à la moyenne du quartier, la tendance et les heures d\'affluence.',
  },
  evenements: {
    Component: EventsLanding,
    title: 'Événements dans l\'app · QRProofs',
    description: 'Publiez votre plat du jour et vos soirées dans l\'app YummiTracker, pour les gourmands du quartier.',
  },
};

async function loginToDashboard() {
  try {
    const keycloak = await initKeycloak();
    await keycloak.login({
      redirectUri: `${window.location.origin}/showDashboard`,
    });
  } catch (err) {
    console.error('Login failed:', err);
  }
}

function setMetaDescription(content) {
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    document.head.appendChild(meta);
  }
  const previous = meta.getAttribute('content');
  meta.setAttribute('content', content);
  return previous;
}

function LandingPage({ page, referralCode, searchParams }) {
  const { Component, title, description } = LANDINGS[page];
  const [demoOpen, setDemoOpen] = useState(false);
  const search = searchParams.toString();

  useEffect(() => {
    const previousTitle = document.title;
    const previousDescription = setMetaDescription(description);
    document.title = title;
    recordLandingVisit(page, new URLSearchParams(search));
    return () => {
      document.title = previousTitle;
      if (previousDescription !== null) setMetaDescription(previousDescription);
    };
  }, [page, title, description, search]);

  return (
    <>
      <Component onLogin={loginToDashboard} onDemo={() => setDemoOpen(true)} />
      <BookDemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} referralCode={referralCode} />
    </>
  );
}

export default function HomePage() {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const page = searchParams.get('page');

  if (page && Object.prototype.hasOwnProperty.call(LANDINGS, page)) {
    return <LandingPage page={page} referralCode={referralCode} searchParams={searchParams} />;
  }
  return <MainHomePage referralCode={referralCode} />;
}

// The design of the main page: a light ground, one blue, and the
// contrast between other review platforms and QRProofs.
const INK = '#0F172A';
const MUTED = '#475569';
const ACCENT = '#1557B0';
const LINE = '#E3E8F0';
const GROUND = '#F6F8FC';
const BAD = '#B42318';
const GOOD = '#0B5D2A';

const pageSx = { maxWidth: 1200, mx: 'auto', px: 3 };
const titleSx = { fontWeight: 800, letterSpacing: -0.3, fontSize: { xs: 28, md: 38 }, color: INK };
const cardSx = {
  bgcolor: '#FFFFFF',
  border: `1px solid ${LINE}`,
  borderRadius: 4,
  p: 3.25,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.25,
};
const primaryButtonSx = {
  bgcolor: ACCENT, color: '#FFFFFF', borderRadius: 3, textTransform: 'none', fontWeight: 700, fontSize: 17, px: 3, py: 1.5,
  '&:hover': { bgcolor: '#0D3F86' },
};
const secondaryButtonSx = {
  bgcolor: '#FFFFFF', color: INK, border: '1px solid #CBD5E1', borderRadius: 3, textTransform: 'none', fontWeight: 700,
  fontSize: 17, px: 3, py: 1.4, '&:hover': { bgcolor: GROUND },
};

// The comparison table: what other platforms let through, what QRProofs checks.
const CONTRAST_ROWS = ['who', 'proof', 'fake', 'customer'];

// The main features, each with the page that tells more (?page=… landings,
// LANDINGS above). `dashboard`: a feature of the dashboard itself.
const FEATURES = [
  { key: 'verified', to: '/?page=avis-verifies', Icon: VerifiedUserOutlinedIcon },
  { key: 'stats', to: '/?page=statistiques', Icon: BarChartIcon },
  { key: 'events', to: '/?page=evenements', Icon: EventNoteOutlinedIcon },
  { key: 'alerts', dashboard: true, Icon: NotificationsNoneOutlinedIcon },
  { key: 'conciergerie', to: '/?page=conciergerie', Icon: HomeOutlinedIcon },
  { key: 'team', to: '/UserGuide', Icon: GroupsOutlinedIcon },
];

function MainHomePage({ referralCode }) {
  const { t, i18n } = useTranslation();

  const [demoOpen, setDemoOpen] = useState(false);
  const openDemoDialog = () => setDemoOpen(true);

  // Logged in already (Keycloak's silent check-sso): the header offers the
  // dashboard and « Déconnexion » instead of « Connexion » / « S'inscrire ».
  const [account, setAccount] = useState(null);
  useEffect(() => {
    let active = true;
    Promise.resolve()
      .then(initKeycloak)
      .then((kc) => { if (active && kc?.authenticated) setAccount({ kc, email: kc.tokenParsed?.email || '' }); })
      .catch((err) => console.warn('[home] session check failed:', err?.message || err));
    return () => { active = false; };
  }, []);
  const handleLogout = () => account?.kc.logout({ redirectUri: window.location.origin });

  const languageOptions = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'es', label: 'Español' },
    { code: 'pt', label: 'Português (Brasil)' },
    { code: 'zh', label: '中文' },
    { code: 'ja', label: '日本語' },
  ];

  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'fr').split('-')[0];

  const changeLanguage = async (event) => {
    const language = event.target.value;
    await i18n.changeLanguage(language);
    localStorage.setItem('i18nextLng', language);
  };

  const handleLogin = loginToDashboard;
  const navLinkSx = { color: '#334155', fontWeight: 500, fontSize: 15, textDecoration: 'none', '&:hover': { color: ACCENT } };

  return (
    <Box sx={{ bgcolor: GROUND, color: INK, minHeight: '100vh' }}>
      {/* ---------- Top Bar ---------- */}
      <Box
        component="header"
        sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(10px)', borderBottom: `1px solid ${LINE}` }}
      >
        <Box sx={{ ...pageSx, py: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px 24px' }}>
          {/* The logo; on phones only its icon, the header is narrow there. */}
          <Box
            component={RouterLink}
            to="/ManagerDashboard"
            aria-label="QRProof"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Box component="img" src="/logo-qrproof.png" alt="QRProof" sx={{ height: 40, display: { xs: 'none', sm: 'block' } }} />
            <Box component="img" src="/logo-qrproof-icon.png" alt="QRProof" sx={{ height: 40, display: { xs: 'block', sm: 'none' } }} />
          </Box>

          <Box component="nav" aria-label={t('home.nav.label')} sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 22px' }}>
            <Link href="#features" sx={navLinkSx}>{t('home.nav.features')}</Link>
            <Link href="#how" sx={navLinkSx}>{t('home.nav.how')}</Link>
            <Link href="#compare" sx={navLinkSx}>{t('home.nav.compare')}</Link>
            <Link component={RouterLink} to="/UserGuide" sx={navLinkSx}>{t('home.nav.guide')}</Link>
          </Box>

          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 118 }}>
              <Select
                value={currentLanguage}
                onChange={changeLanguage}
                inputProps={{ 'aria-label': t('home.languageSelector') }}
                sx={{ bgcolor: '#FFFFFF' }}
              >
                {languageOptions.map(({ code, label }) => (
                  <MenuItem key={code} value={code}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {account ? (
              <>
                {account.email && (
                  <Typography variant="body2" sx={{ color: MUTED, display: { xs: 'none', md: 'block' } }}>
                    {account.email}
                  </Typography>
                )}
                <Button component={RouterLink} to="/showDashboard" sx={{ ...primaryButtonSx, fontSize: 15, px: 2.25, py: 1.1 }}>
                  {t('home.dashboard')}
                </Button>
                <Button onClick={handleLogout} sx={{ ...secondaryButtonSx, fontSize: 15, px: 2, py: 1 }}>
                  {t('home.logout')}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleLogin} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 15, color: ACCENT }}>
                  {t('home.login')}
                </Button>
                <Button component={RouterLink} to="/SignUp" sx={{ ...primaryButtonSx, fontSize: 15, px: 2.25, py: 1.1 }}>
                  {t('home.signup')}
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Box>

      {/* ===================================================== */}
      {/* HERO — the contrast with other review platforms        */}
      {/* ===================================================== */}
      <Box component="section" sx={{ ...pageSx, pt: { xs: 6, md: 9 }, pb: { xs: 6, md: 8 }, display: 'flex', flexDirection: 'column', gap: 4.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '28px 40px' }}>
          <Box sx={{ flex: '1 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: ACCENT }}>
              {t('home.contrast.kicker')}
            </Typography>
            <Typography
              variant="h1"
              sx={{ fontWeight: 800, fontSize: { xs: 34, md: 54 }, lineHeight: 1.08, letterSpacing: -0.8, maxWidth: 860 }}
            >
              {t('home.contrast.title')}
            </Typography>
            <Typography sx={{ fontSize: 19, lineHeight: 1.55, color: '#334155', maxWidth: 640 }}>
              {t('home.heroSubtitle')}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button component={RouterLink} to="/SignUp" sx={primaryButtonSx}>{t('home.getStarted')}</Button>
            <Button onClick={openDemoDialog} sx={secondaryButtonSx}>{t('home.bookDemo')}</Button>
          </Stack>
        </Box>

        <Box id="compare" sx={{ bgcolor: '#FFFFFF', border: `1px solid ${LINE}`, borderRadius: 4.5, overflowX: 'auto', scrollMarginTop: 96 }}>
          <Box component="table" sx={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 17, '& th, & td': { textAlign: 'left', px: 3, py: 2.5 } }}>
            <Box component="thead" sx={{ bgcolor: '#F1F5F9', fontSize: 15 }}>
              <tr>
                <Box component="td" />
                <Box component="th" scope="col" sx={{ color: MUTED, fontWeight: 700 }}>{t('home.contrast.others')}</Box>
                <Box component="th" scope="col" sx={{ color: ACCENT, fontWeight: 700 }}>QRProofs</Box>
              </tr>
            </Box>
            <tbody>
              {CONTRAST_ROWS.map((row) => (
                <Box component="tr" key={row} sx={{ borderTop: `1px solid ${LINE}` }}>
                  <Box component="th" scope="row" sx={{ fontWeight: 600 }}>{t(`home.contrast.rows.${row}.label`)}</Box>
                  <Box component="td" sx={{ color: row === 'customer' ? MUTED : BAD }}>{t(`home.contrast.rows.${row}.others`)}</Box>
                  <Box component="td" sx={{ color: GOOD, fontWeight: 600 }}>{t(`home.contrast.rows.${row}.us`)}</Box>
                </Box>
              ))}
            </tbody>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: MUTED }}>{t('home.contrast.trialNote')}</Typography>
      </Box>

      {/* ===================================================== */}
      {/* HOW IT WORKS — the mechanics in three concrete steps   */}
      {/* ===================================================== */}
      <Box component="section" id="how" sx={{ bgcolor: '#FFFFFF', borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}`, scrollMarginTop: 72 }}>
        <Box sx={{ ...pageSx, py: { xs: 7, md: 9 }, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box>
            <Typography variant="h2" sx={titleSx} gutterBottom>{t('home.howItWorks.title')}</Typography>
            <Typography sx={{ fontSize: 18, color: MUTED }}>{t('home.howItWorks.subtitle')}</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 2.5 }}>
            {['scan', 'rate', 'publish'].map((key, i) => (
              <Box key={key} sx={{ bgcolor: GROUND, borderRadius: 4, p: 3.25, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box
                  sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: ACCENT, color: '#FFFFFF', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 18 }}
                >
                  {i + 1}
                </Box>
                <Typography variant="h3" sx={{ fontSize: 20, fontWeight: 700, mt: 0.5 }}>
                  {t(`home.howItWorks.steps.${key}.title`)}
                </Typography>
                <Typography sx={{ fontSize: 16, lineHeight: 1.55, color: MUTED }}>
                  {t(`home.howItWorks.steps.${key}.description`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ===================================================== */}
      {/* FEATURES — the kiosk first, then the rest              */}
      {/* ===================================================== */}
      <Box component="section" id="features" sx={{ ...pageSx, py: { xs: 7, md: 9 }, display: 'flex', flexDirection: 'column', gap: 4, scrollMarginTop: 72 }}>
        <Box>
          <Typography variant="h2" sx={titleSx} gutterBottom>{t('home.features.title')}</Typography>
          <Typography sx={{ fontSize: 18, color: MUTED }}>{t('home.features.subtitle')}</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', bgcolor: '#FFFFFF', border: `1px solid ${LINE}`, borderRadius: 5, overflow: 'hidden' }}>
          <Box
            component="img"
            src={counterKioskImg}
            alt={t('home.features.kiosk.imageAlt')}
            sx={{ flex: '1 1 420px', minWidth: 0, width: '100%', height: { xs: 240, md: 340 }, objectFit: 'cover', display: 'block' }}
          />
          <Box sx={{ flex: '1 1 380px', minWidth: 0, p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.75 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: ACCENT }}>
              {t('home.features.kiosk.kicker')}
            </Typography>
            <Typography variant="h3" sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.2 }}>
              {t('home.features.kiosk.title')}
            </Typography>
            <Typography sx={{ fontSize: 16, lineHeight: 1.6, color: MUTED }}>{t('home.features.kiosk.text')}</Typography>
            <Link component={RouterLink} to="/?page=borne" sx={{ fontWeight: 700, color: ACCENT }}>{t('home.features.kiosk.link')}</Link>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2.5 }}>
          {FEATURES.map(({ key, to, dashboard, Icon }) => (
            <Box component="article" key={key} sx={cardSx}>
              <Icon sx={{ color: ACCENT, fontSize: 30 }} />
              <Typography variant="h3" sx={{ fontSize: 20, fontWeight: 700 }}>{t(`home.features.${key}.title`)}</Typography>
              <Typography sx={{ fontSize: 16, lineHeight: 1.55, color: MUTED }}>{t(`home.features.${key}.text`)}</Typography>
              {dashboard ? (
                account ? (
                  <Link component={RouterLink} to="/showDashboard" sx={{ mt: 'auto', fontWeight: 700, color: ACCENT }}>{t('home.features.seeDashboard')}</Link>
                ) : (
                  <Link component="button" type="button" onClick={handleLogin} sx={{ mt: 'auto', alignSelf: 'flex-start', fontWeight: 700, fontSize: 16, color: ACCENT }}>
                    {t('home.features.seeDashboard')}
                  </Link>
                )
              ) : (
                <Link component={RouterLink} to={to} sx={{ mt: 'auto', fontWeight: 700, color: ACCENT }}>{t('home.features.learnMore')}</Link>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ===================================================== */}
      {/* CTA */}
      {/* ===================================================== */}
      <Box component="section" sx={{ ...pageSx, pb: { xs: 7, md: 9 } }}>
        <Box
          sx={{
            bgcolor: '#0B3A82', color: '#FFFFFF', borderRadius: 5.5, px: 4, py: 6,
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 3,
          }}
        >
          <Box sx={{ flex: '1 1 480px', minWidth: 0 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: 26, md: 36 }, lineHeight: 1.15, mb: 1.25 }}>
              {t('home.cta.title')}
            </Typography>
            <Typography sx={{ fontSize: 17, lineHeight: 1.55, color: '#DCE7FA' }}>{t('home.cta.description')}</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              component={RouterLink}
              to="/SignUp"
              sx={{ ...primaryButtonSx, bgcolor: '#FFFFFF', color: '#0B3A82', fontWeight: 800, '&:hover': { bgcolor: '#E8EEF9' } }}
            >
              {t('home.cta.button')}
            </Button>
            <Button
              onClick={openDemoDialog}
              sx={{ ...secondaryButtonSx, bgcolor: 'transparent', color: '#FFFFFF', borderColor: '#8FB2E8', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
            >
              {t('home.bookDemo')}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ---------- Footer ---------- */}
      <Box component="footer" sx={{ bgcolor: '#FFFFFF', borderTop: `1px solid ${LINE}` }}>
        <Box sx={{ ...pageSx, py: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px 32px' }}>
          <Box component="img" src="/logo-qrproof.png" alt="QRProof" sx={{ height: 32, display: 'block' }} />
          <Box component="nav" aria-label={t('home.nav.footer')} sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 22px', fontSize: 14 }}>
            <Link component={RouterLink} to="/UserGuide" color="inherit">{t('home.nav.guide')}</Link>
            <Link component={RouterLink} to="/mentions-legales" color="inherit">{t('legalPages.notice')}</Link>
            <Link component={RouterLink} to="/confidentialite" color="inherit">{t('legalPages.privacy')}</Link>
            <Link component={RouterLink} to="/conditions-generales" color="inherit">{t('legalPages.terms')}</Link>
          </Box>
          <Typography variant="body2" sx={{ color: MUTED }}>© {new Date().getFullYear()} QRProof</Typography>
        </Box>
      </Box>

      {/* ---------- Book a demo ---------- */}
      <BookDemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} referralCode={referralCode} />
    </Box>
  );
}
